import { useState, useEffect, useCallback } from "react";
import { DealerLayout } from "@/components/shared/DealerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getKnowledgeBase, getKBCategories, getDealerRepairStatus } from "@/lib/api";
import { motion } from "framer-motion";
import {
  Search, BookOpen, FileText, HelpCircle, Loader2, ChevronLeft, ChevronRight,
  ArrowLeft, Lock, Wrench, Video, X,
} from "lucide-react";

function ArticleViewer({ article, onBack }) {
  const [currentPage, setCurrentPage] = useState(0);
  const pages = article.pages || [];
  const hasPages = pages.length > 0;
  const page = pages[currentPage];

  return (
    <div data-testid="kb-article-viewer">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="kb-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h2 className="font-heading font-bold text-xl text-navy">{article.title}</h2>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{article.category}</span>
            {article.is_repair_content && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Repair Content</span>}
          </div>
        </div>
      </div>

      {hasPages ? (
        <Card className="border-slate-200 mb-6" data-testid="kb-slideshow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-navy">Page {currentPage + 1} of {pages.length}</h3>
              <div className="flex gap-1">
                {pages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${i === currentPage ? "bg-teal" : "bg-slate-200"}`}
                    data-testid={`kb-page-dot-${i}`}
                  />
                ))}
              </div>
            </div>
            {page && (
              <div>
                {page.image_url && (
                  <img src={page.image_url} alt="" className="w-full max-h-80 object-contain rounded-lg mb-4 bg-slate-50" />
                )}
                {page.video_url && (
                  <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
                    <video src={page.video_url} controls className="w-full h-full" />
                  </div>
                )}
                <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">{page.content}</div>
              </div>
            )}
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setCurrentPage(currentPage - 1)} data-testid="kb-slide-prev">
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <Button variant="outline" size="sm" disabled={currentPage === pages.length - 1} onClick={() => setCurrentPage(currentPage + 1)} data-testid="kb-slide-next">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 mb-6">
          <CardContent className="p-6">
            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">{article.content}</div>
          </CardContent>
        </Card>
      )}

      {article.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-4">
          {article.tags.map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full font-body">{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DealerKnowledgeBase() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [repairStatus, setRepairStatus] = useState("none");

  const fetchData = useCallback(async () => {
    try {
      const [data, cats, repairData] = await Promise.all([
        getKnowledgeBase(),
        getKBCategories(),
        getDealerRepairStatus(),
      ]);
      setArticles(data);
      setCategories(cats);
      setRepairStatus(repairData.best_repair_status);
    } catch (error) {
      console.error("Error fetching knowledge base:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const canViewRepairContent = repairStatus === "training" || repairStatus === "approved";

  const filteredArticles = articles.filter((article) => {
    if (article.is_repair_content && !canViewRepairContent) return false;
    const matchesSearch =
      article.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.tags?.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = activeCategory === "all" || article.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const categoryTabs = [
    { value: "all", label: "All Articles" },
    ...categories.map((c) => ({ value: c.value, label: c.label })),
  ];

  if (selectedArticle) {
    return (
      <DealerLayout title="Knowledge Base">
        <div className="max-w-4xl mx-auto" data-testid="dealer-knowledge-base">
          <ArticleViewer article={selectedArticle} onBack={() => setSelectedArticle(null)} />
        </div>
      </DealerLayout>
    );
  }

  return (
    <DealerLayout title="Knowledge Base">
      <div className="space-y-6" data-testid="dealer-knowledge-base">
        {/* Header */}
        <div className="bg-navy rounded-xl p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="font-heading font-bold text-2xl mb-2">Dealer Knowledge Base</h2>
              <p className="text-slate-300 font-body">
                Find answers to common questions and learn best practices for your wireless business.
              </p>
            </div>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                data-testid="kb-search-input"
              />
            </div>
          </div>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2" data-testid="kb-category-filter">
          {categoryTabs.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setActiveCategory(cat.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${activeCategory === cat.value ? "bg-teal text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
              data-testid={`kb-tab-${cat.value}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Articles Grid */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal mx-auto" />
            <p className="text-slate-500 mt-4 font-body">Loading articles...</p>
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-body">No articles found</p>
            <p className="text-slate-400 text-sm font-body">Try adjusting your search or category</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {filteredArticles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card
                  className="border-slate-200 hover:border-teal/30 transition-colors cursor-pointer h-full"
                  onClick={() => setSelectedArticle(article)}
                  data-testid={`kb-article-${article.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${article.is_repair_content ? "bg-amber-50" : "bg-teal/10"}`}>
                        {article.is_repair_content ? <Wrench className="w-5 h-5 text-amber-500" /> : <BookOpen className="w-5 h-5 text-teal" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-semibold text-navy mb-2">{article.title}</h3>
                        <p className="text-slate-500 text-sm font-body line-clamp-3 mb-3">{article.content}</p>
                        <div className="flex flex-wrap gap-1">
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full font-body">{article.category}</span>
                          {article.is_repair_content && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-body">Repair</span>}
                          {article.pages?.length > 0 && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-body">{article.pages.length} pages</span>}
                          {article.tags?.map((tag) => (
                            <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full font-body">{tag}</span>
                          ))}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Quick Help */}
        <Card className="border-slate-200 bg-gradient-to-r from-teal/5 to-navy/5">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-teal rounded-lg flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-navy">Can't find what you're looking for?</h3>
                  <p className="text-slate-500 text-sm font-body">Contact our dealer support team for personalized assistance.</p>
                </div>
              </div>
              <a
                href="mailto:dealersupport@primemediawireless.com"
                className="px-4 py-2 bg-teal hover:bg-teal-dark text-white font-medium rounded-lg transition-colors font-body"
              >
                Contact Support
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </DealerLayout>
  );
}
